/**
 * Vocabulary decks — the single source of truth for both the client and the
 * Worker. The Worker needs them to build duel decks, so they cannot live only
 * in the SPA bundle.
 *
 * Two bodies of vocabulary:
 *
 * - `ielts`: the curated IELTS Academic set, answered in English. The switcher
 *   picks which language it is *prompted* in. Both decks share the same curated
 *   answer list per card, so they can never disagree about what counts as
 *   correct, and progress is tracked separately per prompt language because
 *   recalling "flood" from "inondation" and from "홍수" are different skills.
 * - `korean`: the most common Korean words, prompted in French and answered in
 *   Korean (Hangul or romanised).
 *
 * The `words` table in migrations/0001_init.sql mirrors this shape, so moving
 * to database-backed and user-owned lists later is a data migration rather
 * than a redesign.
 */
import { IELTS_FR, IELTS_KO } from "./ielts";
import { KOREAN_FR } from "./korean";

/**
 * One prompt and every answer accepted for it. `a[0]` is shown on failure.
 * `show`, when present, is what to list as "also accepted" instead of
 * `a.slice(1)` — for answers whose accepted spellings are not all worth
 * displaying.
 */
export type Entry = { w: string; a: string[]; show?: string[] };

export type DeckId = "fr" | "ko" | "kr";

/**
 * A body is one set of cards. Two decks of the same body hold the same cards
 * in the same order, which is what lets a duel deal one set of indices to two
 * players reading different prompt languages.
 */
export type Body = "ielts" | "korean";

export interface Deck {
  id: DeckId;
  body: Body;
  /** Label on the switcher pill in the header. */
  label: string;
  /** BCP-47 tag, set as the `lang` attribute on the prompt word. */
  lang: string;
  /** BCP-47 tag of the answers: the voice to speak them with, the input's lang. */
  answerLang: string;
  /** What the learner is asked to type, for the input placeholder. */
  answerLabel: string;
  /** Uppercase direction label above the word. */
  direction: string;
  entries: Entry[];
}

export const DECKS: Record<DeckId, Deck> = {
  fr: {
    id: "fr",
    body: "ielts",
    label: "Français",
    lang: "fr",
    answerLang: "en-GB",
    answerLabel: "Type the English word",
    direction: "Français → English",
    entries: IELTS_FR,
  },
  ko: {
    id: "ko",
    body: "ielts",
    label: "한국어",
    lang: "ko",
    answerLang: "en-GB",
    answerLabel: "Type the English word",
    direction: "한국어 → English",
    entries: IELTS_KO,
  },
  kr: {
    id: "kr",
    body: "korean",
    label: "Learn Korean",
    lang: "fr",
    answerLang: "ko-KR",
    answerLabel: "Type the Korean word (한글 or romanised)",
    direction: "Français → 한국어",
    entries: KOREAN_FR,
  },
};

/** Decks offered in the header switcher, in display order. */
export const DECK_IDS: DeckId[] = ["fr", "ko", "kr"];

export const DEFAULT_DECK: DeckId = "fr";

export function isDeckId(x: unknown): x is DeckId {
  return typeof x === "string" && x in DECKS;
}

export function getDeck(id: unknown): Deck {
  return isDeckId(id) ? DECKS[id] : DECKS[DEFAULT_DECK];
}

/**
 * Every deck of a body is the same cards in the same order, so index `i` is
 * the same card in every language. That is what lets two players duel on one
 * deal of cards while each reads the prompt in their own language: the room
 * deals indices, and each side renders them from its own deck.
 *
 * Guarded by test/ielts-data.test.ts — break the parallel ordering and a duel
 * silently asks the two players different questions.
 */
export function cardCount(body: Body): number {
  return DECKS[defaultDeckFor(body)].entries.length;
}

/** Cards of the IELTS body; kept for callers that predate the second body. */
export const CARD_COUNT = cardCount("ielts");

/** The deck a player is moved to when their own deck is not of the room's body. */
export function defaultDeckFor(body: Body): DeckId {
  return body === "korean" ? "kr" : DEFAULT_DECK;
}

export function cardsFor(deckId: DeckId, indices: readonly number[]): Entry[] {
  const entries = DECKS[deckId].entries;
  return indices.map((i) => entries[i]).filter((e): e is Entry => e !== undefined);
}
